import React, { useState, useCallback } from "react";
import { i18nCrossUtil } from "@adgm/libs";
import UploadFile from "@aspen/ui/uploadFile/UploadFile";
import { StyleSheet, View, Text } from "../../platformComponents";
import { colors, fontSize, margin, padding, height } from "../../styles/common";
import { CommonImage } from "../../common";
import type { TKycDocumentItem, TKycUploadWebProps } from "@adgm/cross-models";
import { KYC_TEMPLATE_URL_INDIVIDUAL, KYC_TEMPLATE_URL_ENTITY } from "@adgm/cross-config";
import { Button } from "antd";

/**
 * 表格行组件
 */
const KycTableRow: React.FC<{
  rowData: TKycDocumentItem;
  isLastRow?: boolean;
}> = ({ rowData, isLastRow = false }) => {
  const { documentType, multiRowDocumentType, document, alternative, certification, validity } =
    rowData;

  // 渲染文本内容，支持换行
  const renderCellText = (text: string, isBold = false) => {
    const lines = text.split("\n").filter(line => line.trim());
    return lines.map((line, index) => (
      <Text key={index} style={[styles.cellText, isBold && styles.cellTextBold]}>
        {line}
      </Text>
    ));
  };

  // 处理多格合并的第一列
  if (multiRowDocumentType && multiRowDocumentType.length > 0) {
    return (
      <View style={[styles.tableRow, isLastRow && styles.lastRow]}>
        {/* 第一列 - 分为多个格子 */}
        <View
          style={[styles.multiRowCell, styles.highlightedCell, isLastRow && styles.lastRowCell]}>
          {multiRowDocumentType.map((docType, index) => (
            <View key={index} style={styles.multiRowSubCell}>
              <Text style={styles.cellTextBold}>{docType}</Text>
              {/* 添加完整宽度的分隔线 */}
              {index < multiRowDocumentType.length - 1 && (
                <View style={styles.multiRowSubCellBorderLine} />
              )}
            </View>
          ))}
        </View>
        {/* 其他列正常显示 */}
        <View style={[styles.tableCell, styles.multiRowOtherCell, isLastRow && styles.lastRowCell]}>
          {renderCellText(document)}
        </View>
        <View style={[styles.tableCell, styles.multiRowOtherCell, isLastRow && styles.lastRowCell]}>
          {renderCellText(alternative)}
        </View>
        <View style={[styles.tableCell, styles.multiRowOtherCell, isLastRow && styles.lastRowCell]}>
          {renderCellText(certification)}
        </View>
        <View
          style={[
            styles.tableCell,
            styles.multiRowOtherCell,
            isLastRow && styles.lastRowCell,
            styles.lastColumnCell,
          ]}>
          {renderCellText(validity)}
        </View>
      </View>
    );
  }

  // 普通行
  return (
    <View style={[styles.tableRow, isLastRow && styles.lastRow]}>
      <View style={[styles.tableCell, styles.highlightedCell, isLastRow && styles.lastRowCell]}>
        <Text style={styles.cellTextBold}>{documentType}</Text>
      </View>
      <View style={[styles.tableCell, isLastRow && styles.lastRowCell]}>
        {renderCellText(document)}
      </View>
      <View style={[styles.tableCell, isLastRow && styles.lastRowCell]}>
        {renderCellText(alternative)}
      </View>
      <View style={[styles.tableCell, isLastRow && styles.lastRowCell]}>
        {renderCellText(certification)}
      </View>
      <View style={[styles.tableCell, isLastRow && styles.lastRowCell, styles.lastColumnCell]}>
        {renderCellText(validity)}
      </View>
    </View>
  );
};

export const KycUpload: React.FC<TKycUploadWebProps> = props => {
  const { onSubmit, kycType, loading } = props;
  const isIndividual = kycType === "individuals";
  const crossIntl = i18nCrossUtil.t();
  const [selfCertificationFiles, setSelfCertificationFiles] = useState<string[]>([]);
  const [documentFiles, setDocumentFiles] = useState<string[]>([]);

  const handleSelfCertificationUpload = useCallback((urlList: string[]) => {
    setSelfCertificationFiles(urlList);
  }, []);

  const handleDocumentUpload = useCallback((urlList: string[]) => {
    setDocumentFiles(urlList);
  }, []);

  const handleDownloadTemplate = useCallback(() => {
    window.open(isIndividual ? KYC_TEMPLATE_URL_INDIVIDUAL : KYC_TEMPLATE_URL_ENTITY, "_blank");
  }, []);

  const handleSubmit = useCallback(() => {
    const allFiles = isIndividual ? documentFiles : [...selfCertificationFiles, ...documentFiles];
    onSubmit?.(allFiles);
  }, [selfCertificationFiles, documentFiles, onSubmit, isIndividual]);

  // 获取当前类型的表格数据
  const tableData: TKycDocumentItem[] = isIndividual
    ? crossIntl["kyc.upload.individual.table"] || []
    : crossIntl["kyc.upload.institutions.table"] || [];

  return (
    <View style={styles.container}>
      {/* Upload Self Certification Form Section */}
      {!isIndividual ? (
        <View style={styles.section}>
          <Text style={styles.title}>{crossIntl["kyc.upload.self.certification.title"]}</Text>

          <View style={styles.formContent}>
            <View style={styles.instructionRow}>
              <Text style={styles.required}>*</Text>
              <View style={styles.instructionContent}>
                <Text style={styles.instruction}>
                  {crossIntl["kyc.upload.download.instruction"]}
                </Text>
                <View style={styles.downloadButtonContainer}>
                  <CommonImage
                    source={require("../../assets/images/ic_down.webp")}
                    width={14}
                    height={14}
                  />
                  <Text onPress={handleDownloadTemplate} style={styles.downloadButton}>
                    {crossIntl["kyc.upload.form.template"]}
                  </Text>
                </View>
              </View>
            </View>

            {/* Upload Area */}
            <View style={styles.uploadContainer}>
              <UploadFile
                type="public/documents"
                text={crossIntl["kyc.upload.drag.text"]}
                getUploadInfo={handleSelfCertificationUpload}
              />
            </View>
          </View>
        </View>
      ) : undefined}

      {/* Upload Documents Section */}
      <View style={styles.documentsSection}>
        <Text style={styles.title}>{crossIntl["kyc.upload.documents.title"]}</Text>

        {/* Documents Table */}
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            <View style={[styles.tableCell, styles.headerCell]}>
              <Text style={styles.headerText}>{crossIntl["kyc.table.document.type"]}</Text>
            </View>
            <View style={[styles.tableCell, styles.headerCell]}>
              <Text style={styles.headerText}>{crossIntl["kyc.table.document"]}</Text>
            </View>
            <View style={[styles.tableCell, styles.headerCell]}>
              <Text style={styles.headerText}>{crossIntl["kyc.table.alternative"]}</Text>
            </View>
            <View style={[styles.tableCell, styles.headerCell]}>
              <Text style={styles.headerText}>{crossIntl["kyc.table.certification"]}</Text>
            </View>
            <View style={[styles.tableCell, styles.headerCell, styles.headerLastCell]}>
              <Text style={styles.headerText}>{crossIntl["kyc.table.validity"]}</Text>
            </View>
          </View>

          {/* Table Rows */}
          {tableData.map((rowData, index) => (
            <KycTableRow key={index} rowData={rowData} isLastRow={index === tableData.length - 1} />
          ))}
        </View>

        {/* Bottom Upload Area */}
        <View style={styles.uploadContainer}>
          <UploadFile
            type="public/documents"
            text={crossIntl["kyc.upload.drag.text"]}
            getUploadInfo={handleDocumentUpload}
          />
        </View>

        {/* Submit Button */}
        <Button
          style={styles.submitButton}
          loading={loading}
          disabled={
            (!isIndividual && selfCertificationFiles.length === 0) ||
            documentFiles.length === 0 ||
            loading
          }
          type="primary"
          htmlType="submit"
          onClick={handleSubmit}>
          {crossIntl["button.next"]}
        </Button>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
  },
  section: {
    marginBottom: margin.m40,
  },
  documentsSection: {
    marginBottom: margin.m40,
    maxWidth: 1081,
  },
  title: {
    fontSize: fontSize.f20,
    fontWeight: "bold",
    color: colors.white,
    marginBottom: margin.m24,
    lineHeight: 24,
  },
  formContent: {
    gap: margin.m24,
  },
  instructionRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    width: "100%",
  },
  required: {
    color: colors.red2,
    fontSize: fontSize.f14,
    marginRight: margin.m4,
  },
  instructionContent: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: margin.m16,
  },
  instruction: {
    color: colors.white,
    fontSize: fontSize.f20,
  },
  downloadButton: {
    color: colors.highlightColor,
    fontSize: fontSize.f16,
    fontWeight: "500",
    textDecorationLine: "underline",
    marginLeft: margin.m4,
  },

  uploadContainer: {
    marginTop: margin.m16,
    width: 300,
  },
  table: {
    width: "100%",
    marginBottom: margin.m40,
    borderWidth: 1,
    borderColor: "#4d4e57",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#252f53",
  },
  tableRow: {
    flexDirection: "row",
  },
  tableCell: {
    flex: 1,
    padding: padding.p16,
    borderRightWidth: 1,
    borderRightColor: "#4d4e57",
    borderBottomWidth: 1,
    borderBottomColor: "#4d4e57",
    minHeight: height.h56,
    justifyContent: "center",
  },
  headerCell: {
    backgroundColor: "#252f53",
  },
  highlightedCell: {
    backgroundColor: "rgba(255,255,255,0.05)",
  },
  headerText: {
    color: colors.white,
    fontSize: fontSize.f14,
  },
  cellText: {
    color: colors.white,
    fontSize: fontSize.f14,
    lineHeight: 22,
  },
  cellTextBold: {
    color: colors.white,
    fontSize: fontSize.f14,
    fontWeight: "bold",
    lineHeight: 22,
  },
  // 多格合并行相关样式
  multiRowCell: {
    flex: 1, // 确保与普通cell相同的flex基础
    flexDirection: "column",
    padding: padding.p16, // 与普通cell保持相同的padding
    borderRightWidth: 1,
    borderRightColor: "#4d4e57",
    borderBottomWidth: 1,
    borderBottomColor: "#4d4e57",
    justifyContent: "center",
  },
  multiRowSubCell: {
    flex: 1,
    padding: 0, // 子格不再需要padding，由父容器提供
    justifyContent: "center",
    minHeight: 60, // 调整子格高度，因为父容器现在有padding
    position: "relative", // 为了定位border线条
  },

  multiRowSubCellBorderLine: {
    position: "absolute",
    bottom: 0,
    left: -padding.p16, // 向左扩展到父容器边缘
    right: -padding.p16, // 向右扩展到父容器边缘
    height: 1,
    backgroundColor: "#4d4e57",
  },
  multiRowOtherCell: {
    minHeight: 212, // 匹配多格合并第一列的总高度 (3 * 60 + 2 * 16 padding)
  },
  lastRow: {
    marginBottom: 0,
  },
  submitButton: {
    width: 140,
    height: 32,
    marginTop: margin.m20,
  },
  downloadButtonContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  lastRowCell: {
    borderBottomWidth: 0,
  },
  headerLastCell: {
    borderRightWidth: 0,
  },
  lastColumnCell: {
    borderRightWidth: 0,
  },
});
